import { useCallback, useEffect, useMemo, useReducer } from "react";

import { useNode } from "@craftjs/core";

import { useCraftProperty } from "@/components/builder/core/hooks";
import createContextFactory from "@/utils/factory"

import { useOptions } from "./useOptions";

const { Provider: GlobalStateProvider, useContext: useGlobalStateContext } = createContextFactory(() => {
  const [states, registerState] = useReducer((states, { key, remove = false, ...data }) => {
    if (remove) {
      const { [key]: _, ...rest } = states
      return rest
    }

    return {
      ...states,
      [key]: data,
    }
  }, {})

  return useMemo(() => ({
    states: Object.values(states),
    get hasStates() {
      return this.states.length > 0
    },
    registerState,
  }), [states])
})

const { Provider: StateProvider, useContext: useStateContext } = createContextFactory(({ label, prop }) => {
  const { registerState } = useGlobalStateContext()

  const show = useCraftProperty(`properties.${prop}`)
  const options = useOptions(show)

  const register = useCallback((key, data) => {
    registerState({ key, ...data })
    return () => {
      registerState({ key, remove: true })
    }
  }, [registerState])

  return useMemo(() => ({
    show,
    label,
    prop,
    options,
    register,
  }), [show, label, prop, options, register])
})

export function useRegisterState() {
  const { id, StateComponent } = useNode(({ related: { StateComponent } }) => ({ StateComponent }))
  const { label, prop, register } = useStateContext()

  useEffect(() => register(id, { label, prop, Component: StateComponent }), [register, id, label, prop, StateComponent])
}

export function StateRegisterWrapper({ children }) {
  useRegisterState()
  return children
}

export {
  useGlobalStateContext,
  GlobalStateProvider,
  useStateContext,
  StateProvider,
}
